import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import type { RentRollReconciliation as Reconciliation, RentRollReconciliationField, RentRollReconciliationItem } from '../api/schema'
import { useFormatters } from '../lib/format'
import { Badge } from './ui/badge'
import { Card, CardContent } from './ui/card'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { Skeleton } from './ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'

const STATUSES = ['match', 'differs', 'occupancy_differs', 'only_in_statement', 'only_in_leases'] as const

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
    match: 'success',
    differs: 'warning',
    occupancy_differs: 'destructive',
    only_in_statement: 'secondary',
    only_in_leases: 'secondary',
}

/**
 * The "Compare with a rent roll" select of the rent roll tab (spec
 * 2026-10-27-15 §5): the ERP rent rolls filed under the asset, newest first.
 * Choosing one shows its comparison with the leases.
 */
export function RentRollCompare({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate } = useFormatters()
    const [statementUid, setStatementUid] = useState('')
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/leases/rent-roll/statements', {
        params: { path: { orgCode: org }, query: { asset_uid: assetUid } },
    })
    const statements = data?.data ?? []

    return (
        <div className="space-y-3" data-testid="rent-roll-compare-section">
            <div>
                <Label className="mb-1 block text-sm" htmlFor="rent-roll-compare">{t('rent_roll.compare.label')}</Label>
                <select
                    id="rent-roll-compare"
                    data-testid="rent-roll-compare"
                    value={statementUid}
                    disabled={isLoading || statements.length === 0}
                    onChange={(e) => setStatementUid(e.target.value)}
                    className="w-full max-w-md rounded border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm"
                >
                    <option value="">{t('rent_roll.compare.none')}</option>
                    {statements.map((s) => (
                        <option key={s.uid} value={s.uid} data-testid="rent-roll-compare-option">
                            {s.name} — {formatDate(s.doc_date ?? s.created_at)}
                        </option>
                    ))}
                </select>
                {!isLoading && statements.length === 0 && (
                    <p className="mt-1 text-sm text-muted-foreground" data-testid="rent-roll-compare-empty">
                        {t('rent_roll.compare.empty')}
                    </p>
                )}
            </div>
            {statementUid && <RentRollReconciliation org={org} assetUid={assetUid} statementUid={statementUid} />}
        </div>
    )
}

/**
 * The comparison of an ERP rent roll with the leases: the counts per status,
 * then one row per statement line and per lease left unmatched, with the
 * values that differ. Read only: a lease is corrected on its page.
 */
export function RentRollReconciliation({ org, assetUid, statementUid }: { org: string; assetUid: string; statementUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate, formatMoney } = useFormatters()
    const [differencesOnly, setDifferencesOnly] = useState(false)
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/leases/rent-roll/reconciliation', {
        params: { path: { orgCode: org }, query: { asset_uid: assetUid, statement_uid: statementUid } },
    })
    const rec = data as Reconciliation | undefined
    if (isLoading) return <Skeleton className="h-32 w-full" />
    if (!rec) return null

    const items = differencesOnly ? rec.items.filter((it) => it.status !== 'match') : rec.items
    const currencyOf = (it: RentRollReconciliationItem) => it.line?.currency ?? it.lease?.currency ?? undefined
    const value = (field: string, v: unknown, it: RentRollReconciliationItem): string => {
        if (v === null || v === undefined || v === '') return '—'
        if (field === 'annual_rent' && typeof v === 'number') return formatMoney(v, currencyOf(it))
        if (Array.isArray(v)) return v.join(' + ')
        if ((field === 'start_date' || field === 'end_date' || field === 'next_break') && typeof v === 'string') return formatDate(v)
        return String(v)
    }
    const lotsOf = (it: RentRollReconciliationItem) => {
        if (it.line) return (it.line.lots.length > 0 ? it.line.lots.map((l) => l.name) : it.line.lot_labels).join(' + ')
        return (it.lease?.lots ?? []).map((l) => l.name).join(' + ')
    }
    const tenantOf = (it: RentRollReconciliationItem) =>
        it.line ? (it.line.vacant ? t('rent_roll.vacant') : it.line.tenant_name ?? '—') : (it.lease?.tenants ?? []).join(', ') || '—'

    return (
        <div className="space-y-3" data-testid="rent-roll-reconciliation">
            <div className="flex flex-wrap items-center gap-2" data-testid="reconciliation-counts">
                {STATUSES.map((s) => (
                    <Badge key={s} variant={STATUS_VARIANT[s]} data-testid={`reconciliation-count-${s}`} data-count={rec.counts[s]}>
                        {t(`rent_roll.compare.status.${s}`)}: {rec.counts[s]}
                    </Badge>
                ))}
                <span className="text-sm text-muted-foreground">{t('rent_roll.compare.at', { date: formatDate(rec.at_date) })}</span>
            </div>
            <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={differencesOnly} onCheckedChange={setDifferencesOnly} data-testid="reconciliation-only-differences" />
                {t('rent_roll.compare.only_differences')}
            </label>
            <Card>
                <CardContent className="overflow-x-auto p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('rent_roll.compare.column.status')}</TableHead>
                                <TableHead>{t('rent_roll.column.lots')}</TableHead>
                                <TableHead>{t('rent_roll.column.tenant')}</TableHead>
                                <TableHead>{t('rent_roll.compare.column.field')}</TableHead>
                                <TableHead>{t('rent_roll.compare.column.statement')}</TableHead>
                                <TableHead>{t('rent_roll.compare.column.leases')}</TableHead>
                                <TableHead>{t('rent_roll.compare.column.links')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((it, i) => {
                                const fields: RentRollReconciliationField[] = it.fields.length > 0 ? it.fields : [{ field: 'annual_rent', statement: it.line?.annual_rent_minor ?? null, leases: it.lease?.annual_rent_minor ?? null }]
                                return (
                                    <TableRow key={it.line?.uid ?? it.lease?.lease_uid ?? i} data-testid="reconciliation-row" data-status={it.status}>
                                        <TableCell>
                                            <Badge variant={STATUS_VARIANT[it.status]} data-testid="reconciliation-row-status">
                                                {t(`rent_roll.compare.status.${it.status}`)}
                                            </Badge>
                                            {it.source_only_statement && (
                                                <div className="mt-1 text-xs text-muted-foreground" data-testid="reconciliation-source-only">
                                                    {t('rent_roll.compare.source_only_statement')}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="min-w-32">{lotsOf(it) || '—'}</TableCell>
                                        <TableCell>{tenantOf(it)}</TableCell>
                                        <TableCell className="space-y-1">
                                            {fields.map((f) => (
                                                <div key={f.field}>{t(`rent_roll.compare.field.${f.field}`)}</div>
                                            ))}
                                        </TableCell>
                                        <TableCell className="space-y-1 tabular-nums" data-testid="reconciliation-row-statement">
                                            {fields.map((f) => (
                                                <div key={f.field}>{it.line ? value(f.field, f.statement, it) : '—'}</div>
                                            ))}
                                        </TableCell>
                                        <TableCell className="space-y-1 tabular-nums" data-testid="reconciliation-row-leases">
                                            {fields.map((f) => (
                                                <div key={f.field}>{it.lease ? value(f.field, f.leases, it) : '—'}</div>
                                            ))}
                                        </TableCell>
                                        <TableCell className="space-x-2 whitespace-nowrap text-sm">
                                            {it.lease?.lease_uid && (
                                                <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: it.lease.lease_uid }} className="hover:underline" data-testid="reconciliation-row-lease-link">
                                                    {t('rent_roll.compare.open_lease')}
                                                </Link>
                                            )}
                                            {it.line && (
                                                <Link to="/$org/documents/$docUid" params={{ org, docUid: rec.statement_uid }} className="hover:underline" data-testid="reconciliation-row-document-link">
                                                    {t('rent_roll.compare.open_document')}
                                                </Link>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    )
}
